import { RootState } from 'store';
import SubmitButtons from 'components/Dashboard/components/SurveyPage/components/SubmitButtons/SubmitButtons';
import useQuestionForm from 'components/Dashboard/components/SurveyPage/hooks/useQuestionForm';
import { QuestionPageProps } from 'components/Dashboard/components/SurveyPage/types';
import { Survey, SurveyV1 } from 'api/User/types';
import styles from 'components/Dashboard/components/SurveyPage/components/inputStyles.module.css';

import React, { useCallback, useEffect, useState } from 'react';
import { Box, FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const BloodPressureMedicationsQuestion: React.FC<QuestionPageProps> = ({ onNext }) => {
  const { t } = useTranslation();
  
  const { survey } = useSelector((store: RootState) => store.user.myUser);

  const [ value, setValue ] = useState('');
  const [ medicationName, setMedicationName ] = useState('');

  const { submit, isSubmitting, submitError } = useQuestionForm();

  const onFormSubmit = useCallback((event) => {
    event.preventDefault();

    submit({
      ...survey,
      bloodPressureMedications: { value, name: medicationName },
    })
      .then((data: Survey) => onNext(data));
  }, [ medicationName, onNext, submit, survey, value ]);

  const onRadioChanged = useCallback((event) => {
    setValue(event.target.value);
  }, []);

  const onMedicationChanged = useCallback((event) => {
    setMedicationName(event.target.value);
  }, []);

  useEffect(() => {
    setValue((survey as SurveyV1)?.bloodPressureMedications?.value || '');
    setMedicationName((survey as SurveyV1)?.bloodPressureMedications?.name || '');
  }, [ survey ]);

  return (
    <form onSubmit={onFormSubmit} data-testid="question-blood-pressure-medications">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="textXLBold" color="primary">
            {t('dashboard.survey.question_blood_pressure_medications.question')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <RadioGroup
              onChange={onRadioChanged}
              value={value}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel value="no" control={<Radio />} label={t('general.no')} data-testid="option-no" />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    value="yes"
                    classes={{
                      label: styles.otherRadioWrapper,
                      root: styles.otherRadioWrapper,
                    }}
                    control={<Radio />}
                    data-testid="option-yes"
                    label={(
                      <Box display="flex" justifyContent="center" alignItems="center" className={styles.otherInputWrapper}>
                        <span>{t('general.yes')}</span>
                        <TextField
                          sx={{ marginLeft: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 300 }, marginTop: { xs: 1, sm: 0 } }}
                          variant="outlined"
                          size="small"
                          color="primary"
                          label={t('dashboard.survey.hint_which_ones')}
                          value={medicationName}
                          onChange={onMedicationChanged}
                          disabled={value !== 'yes'}
                          inputProps={{ 'data-testid': 'input-yes' }}
                        />
                      </Box>
                    )}
                  />
                </Grid>
              </Grid>
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item>
          <Typography variant="body1" color="primary" maxWidth={425} fontWeight={400}>
            {t('dashboard.survey.hint_leave_blank')}
          </Typography>
        </Grid>
        {submitError && (
          <Grid item xs={12}>
            <Typography color="error">
              {submitError}
            </Typography>
          </Grid>
        )}
        <SubmitButtons submitting={isSubmitting} />
      </Grid>
    </form>
  );
};

export default BloodPressureMedicationsQuestion;
